export interface PaymentService {
  country: { code: string; name: string };
  currency: { code: string; name: string };
  operation: string;
  payment_method: string;
  method_type?: "CRYPTO" | "MOBILE_MONEY" | string;
  accepted_coins?: string[];
  crypto_asset_code?: string | null;
  otp_required?: boolean;
  provider_name?: string;
  available: boolean;
}

export interface ServiceCatalog {
  environment?: string;
  services: PaymentService[];
}

/** Generic rails are not selectable operators; expose concrete provider methods only. */
export function isGenericPaymentMethod(value: string | null | undefined): boolean {
  const normalized = (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return ["mobile_money", "mobilemoney", "mmo"].includes(normalized);
}

export function extractServices(
  catalog: ServiceCatalog | PaymentService[] | undefined | null,
): PaymentService[] {
  if (!catalog) return [];
  if (Array.isArray(catalog)) return catalog;
  if (Array.isArray(catalog.services)) return catalog.services;
  return [];
}

export function isCryptoPaymentService(service: PaymentService): boolean {
  const method = service.payment_method.trim();
  return (
    service.method_type?.toUpperCase() === "CRYPTO" ||
    method.toLowerCase() === "izipay" ||
    /^(BTC|ETH|USDT|USDC|BNB|SOL|XRP)([._-][A-Z0-9]+)?$/i.test(method)
  );
}

export function availablePaymentMethods(
  catalog: ServiceCatalog | PaymentService[] | undefined,
  country: string,
  currency: string,
  kind: "DEPOSIT" | "WITHDRAWAL",
) {
  const operations =
    kind === "DEPOSIT"
      ? ["DEPOSIT", "COLLECTION", "PAYIN"]
      : ["WITHDRAWAL", "DISTRIBUTION", "PAYOUT"];
  const targetCountry = (country || "").toUpperCase();
  const targetCurrency = (currency || "").toUpperCase();

  return [
    ...new Set(
      extractServices(catalog)
        .filter(
          (service) =>
            service.available !== false &&
            (service.country?.code ?? "").toUpperCase() === targetCountry &&
            (service.currency?.code ?? "").toUpperCase() === targetCurrency &&
            operations.includes((service.operation ?? "").toUpperCase()) &&
            (!isCryptoPaymentService(service) || targetCurrency === "USD"),
        )
        .map((service) => service.payment_method)
        .filter((method) => Boolean(method) && !isGenericPaymentMethod(method)),
    ),
  ];
}
